import http from "services/http";

export type EsimOrderStatus =
  | "pending_payment"
  | "processing"
  | "completed"
  | "failed"
  | "expired"
  | "refund_requested"
  | "refunded";

export type EsimOrderKind = "esim" | "topup";

export interface EsimSim {
  iccid: string;
  qrcode?: string;
  qrcode_url?: string;
  lpa?: string;
  matching_id?: string;
  confirmation_code?: string;
  apn_type?: string;
  apn_value?: string;
  apn?: Partial<Record<"ios" | "android", { apn_type?: string; apn_value?: string }>>;
  direct_apple_installation_url?: string;
  is_roaming?: boolean;
}

export interface EsimOperator {
  id?: number;
  title?: string;
  type?: string;
  plan_type?: string;
  esim_type?: string;
  activation_policy?: string;
  rechargeability?: boolean;
  is_roaming?: boolean;
  is_kyc_verify?: boolean;
  coverages?: { name?: string; code?: string; networks?: { name: string; types?: string[] }[] }[];
  info?: string[];
  other_info?: string | null;
  warning?: string | null;
  install_window_days?: number;
  topup_grace_window_days?: number;
  gradient_start?: string;
  gradient_end?: string;
}

export interface EsimPackage {
  package_id?: string;
  destination_title?: string;
  destination_slug?: string;
  destination_image?: string;
  country_code?: string;
  title?: string;
  data?: string;
  day?: number;
  amount_mb?: number;
  is_unlimited?: boolean;
  operator?: EsimOperator;
}

export interface EsimTransaction {
  _id?: string;
  amount?: number;
  status?: string;
  accountNumber?: string;
  accountName?: string;
  bankName?: string;
  transactionDate?: string;
  transactionReference?: string;
  paymentReference?: string;
}

export interface EsimAccount {
  _id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: number | string;
}

export interface EsimPaymentDetails {
  bankName?: string;
  accountNumber?: string;
  accountName?: string;
  amount?: number;
  expiresOn?: string;
  transactionReference?: string;
}

// Admin responses are undocumented, so every field is optional.
export interface EsimOrder {
  _id?: string;
  order_ref: string;
  status: EsimOrderStatus | string;
  kind?: EsimOrderKind;
  total_price?: number;
  currency?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  iccid?: string;
  payment_details?: EsimPaymentDetails | null;
  sims?: EsimSim[];
  package?: EsimPackage;
  package_id?: string;
  package_snapshot?: EsimPackage;
  customer_name?: string;
  guest?: { first_name?: string; last_name?: string; phone?: string };
  user_id?: EsimAccount | string | null;
  transaction_id?: (EsimTransaction | string)[];
  price_usd?: number;
  net_price_usd?: number;
  airalo_net_price_usd?: number;
  usd_ngn_rate?: number;
  markup_percent?: number;
  charges?: number;
  fulfilment_attempts?: number;
  alerts_sent?: any[];
  airalo_code?: string;
  airalo_order_id?: number | string;
  processing_started_at?: string | null;
  completedAt?: string | null;
  last_error?: string | null;
  financeStatus?: string;
  topups?: EsimOrder[];
  ledger?: Record<string, any>[];
  ledger_entries?: Record<string, any>[];
  created_at?: string;
  createdAt?: string;
  [key: string]: any;
}

export interface EsimOrdersQuery {
  status?: string;
  kind?: string;
  search?: string;
  page: number;
  limit: number;
}

export interface Paged<T> {
  items: T[];
  total: number;
}

export const ESIM_STATUS_TONES = {
  pending_payment: "pending",
  processing: "info",
  completed: "success",
  failed: "error",
  expired: "muted",
  refund_requested: "warning",
  refunded: "muted",
} as const;

export const ESIM_STATUSES = Object.keys(ESIM_STATUS_TONES) as EsimOrderStatus[];

// The pagination shape isn't documented; look in the usual places.
export const normalizeList = <T>(res: any, key: string): Paged<T> => {
  const d = res?.data;
  const items: T[] =
    (Array.isArray(d) && d) ||
    d?.[key] ||
    d?.docs ||
    d?.items ||
    d?.results ||
    [];
  const total = Number(
    d?.total ?? d?.meta?.total ?? d?.pagination?.total ?? d?.totalDocs ?? items.length,
  );
  return { items: Array.isArray(items) ? items : [], total };
};

const clean = (params: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );

export const orderDate = (order: EsimOrder) => order.createdAt || order.created_at;

export const orderIccid = (order: EsimOrder) => order.iccid || order.sims?.[0]?.iccid;

// Orders store the plan as package_snapshot; older responses used package.
export const orderPackage = (order: EsimOrder): EsimPackage => ({
  package_id: order.package_id,
  ...(order.package || {}),
  ...(order.package_snapshot || {}),
});

export const orderCustomerName = (order: EsimOrder) =>
  order.customer_name ||
  [order.guest?.first_name ?? order.first_name, order.guest?.last_name ?? order.last_name]
    .filter(Boolean)
    .join(" ");

export const orderPhone = (order: EsimOrder) => order.guest?.phone || order.phone;

// Only populated transactions are objects; unpopulated ones are bare ids.
export const orderTransaction = (order: EsimOrder) =>
  order.transaction_id?.find((t): t is EsimTransaction => typeof t === "object" && t !== null);

export const orderAccount = (order: EsimOrder) =>
  order.user_id && typeof order.user_id === "object" ? order.user_id : undefined;

// What Airalo charged us in naira at the order's rate; undefined if either is missing.
export const orderCostNgn = (order: EsimOrder) => {
  const net = order.airalo_net_price_usd ?? order.net_price_usd;
  return net !== undefined && order.usd_ngn_rate ? net * order.usd_ngn_rate : undefined;
};

export const getEsimOrders = async (query: EsimOrdersQuery) => {
  const response = await http.get("/esim/admin/orders", { params: clean({ ...query }) });
  return normalizeList<EsimOrder>(response.data, "orders");
};

// The detail may come back as the order itself or wrapped with its ledger.
export const getEsimOrder = async (orderRef: string): Promise<EsimOrder> => {
  const response = await http.get(`/esim/admin/orders/${encodeURIComponent(orderRef)}`);
  const d = response.data?.data || {};
  if (d.order) {
    return {
      ...d.order,
      ledger: d.ledger ?? d.ledger_entries ?? d.order.ledger,
      topups: d.topups ?? d.order.topups,
    };
  }
  return d;
};

export const retryEsimOrder = async (orderRef: string) => {
  const response = await http.post(`/esim/admin/orders/${encodeURIComponent(orderRef)}/retry`);
  return response.data;
};

export const requestEsimRefund = async (
  orderRef: string,
  body: { reason: string; notes?: string },
) => {
  const response = await http.post(
    `/esim/admin/orders/${encodeURIComponent(orderRef)}/refund`,
    body,
  );
  return response.data;
};

export interface ResolveRefundBody {
  customer_refund_ngn: number;
  airalo_approved: boolean;
  airalo_credit_usd: number;
  notes?: string;
}

export const resolveEsimRefund = async (orderRef: string, body: ResolveRefundBody) => {
  const response = await http.post(
    `/esim/admin/orders/${encodeURIComponent(orderRef)}/refund/resolve`,
    body,
  );
  return response.data;
};

// Airalo prepaid credit in USD, at data.balances.availableBalance.amount.
// Returns null (not undefined) when missing — React Query treats undefined as an error.
export const getAiraloBalance = async (): Promise<number | null> => {
  const response = await http.get("/esim/admin/balance");
  const d = response.data?.data;
  const value =
    typeof d === "number"
      ? d
      : d?.balances?.availableBalance?.amount ??
        d?.balance?.amount ??
        d?.balance ??
        d?.amount ??
        d?.available_balance;
  return value === undefined || value === null ? null : Number(value);
};

export const syncEsimPackages = async () => {
  const response = await http.post("/esim/admin/sync-packages");
  return response.data;
};

export const optInEsimNotifications = async (body: {
  webhook_url: string;
  credit_limit_levels: number[];
}) => {
  const response = await http.post("/esim/admin/notifications/opt-in", body);
  return response.data;
};

export const simulateEsimWebhook = async (body: {
  event: string;
  type: string;
  iccid: string;
  webhook_url: string;
}) => {
  const response = await http.post("/esim/admin/simulate-webhook", body);
  return response.data;
};
